import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, TabBar, TabItem } from '@wot/components';
import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import enquiryApi from '../../../api/enquiry';
import { SPINNER } from '../../../assets/animated';
import NoResults from '../../../components/NoResults';
import Pagination from '../../../components/Pagination';
import usePagainate from '../../../hooks/usePaginate';
import { useTab } from '../../../hooks/useTab';
import TableContainer from './components/Table';
import { useClickOutside } from '@wot/shared';
import { FILTER_SVG } from '../../../assets/icons';

interface Filters {
  subject: 'Account Support' | 'General Support' | 'Payment Support' | ""
}

export type Sorting = 'createdAt' | 'name'

export default function SupportEnquiries() {
  const queryClient = useQueryClient();
  const { activeTab, onTabChange } = useTab('active');
  const { currentPage, resultsPerPage, nextPage, prevPage, setResultsPerPage, resetPage } = usePagainate();
  const [sortBy, setSortBy] = useState<{ [key: string]: number }>({ "createdAt": -1 });
  const [filters, setFilters] = useState<Filters | {}>({});

  const handleSort = (key: 'createdAt' | 'name') => {
    setSortBy({ [key]: sortBy[key] === 1 ? -1 : 1 });
  };

  useEffect(() => {
    resetPage();
  }, [activeTab]);

  const { data, isLoading, isError, isPreviousData } = useQuery({
    queryKey: ['enquiries', currentPage, resultsPerPage, activeTab, sortBy, filters],
    queryFn: async () =>
      await enquiryApi.get(currentPage, resultsPerPage, { status: activeTab, type: 'support', ...filters }, { ...sortBy })
  });

  useEffect(() => {
    if (!isPreviousData) {
      queryClient.prefetchQuery({
        queryKey: ['enquiries', currentPage + 1],
        queryFn: async () =>
          await enquiryApi.get(currentPage, resultsPerPage, { status: activeTab, type: 'support' }, { ...sortBy })
      });
    }
  }, [data, isPreviousData, currentPage, resultsPerPage, queryClient, isError]);

  const filterResults = (filters: any) => {
    setFilters(filters);
  }

  if (isError) {
    return (
      <div className="w-full min-h-[256px] flex items-center justify-center">
        <p className="font-medium">Something went Wrong.</p>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-4 p-2 grow">
      <h1 className="text-xl font-semibold">Support Management</h1>
      <TabBar value={activeTab} onChange={onTabChange} textColor="text-black" indicatorColor="bg-primary">
        <TabItem label="Active" value="active" />
        <TabItem label="Resolved" value="resolved" />
      </TabBar>
      <Filters onApply={filterResults} onClear={() => setFilters({})} filters={filters} />
      {isLoading ? (
        <div className="w-full min-h-[256px] flex items-center justify-center">
          <img src={SPINNER} alt="loading" className="w-6 h-6" />
        </div>
      ) : data.data.length > 0 ? (
        <>
          <TableContainer handleSort={handleSort} data={(data.data && data.data) || []} />
          <Pagination
            count={data.count}
            currentPage={currentPage}
            nextPage={nextPage}
            prevPage={prevPage}
            resultsPerPage={resultsPerPage}
            setResultsPerPage={setResultsPerPage}
          />
        </>
      ) : (
        <NoResults />
      )}
      <Outlet />
    </div>
  );
}


interface Props {
  onApply: (filters: any) => void
  onClear: () => void,
  filters: Filters | {}
}
function Filters({ filters, onApply, onClear }: Props) {
  const [showOptions, setShowOptions] = useState(false);
  const filterSortRef = useRef(null);

  const [temp, setTemp] = useState<Filters>({
    subject: ""
  });

  useEffect(() => {
    if (Object.keys(filters).length > 0) {
      // @ts-ignore
      setTemp(filters);
    }
  }, [filters]);

  useClickOutside(filterSortRef, () => setShowOptions(false));

  return (
    <div className='relative flex justify-end'>
      <div onClick={() => setShowOptions(true)} className={`w-7 h-7 ${Object.keys(filters).length > 0 ? "bg-blue-100" : "bg-white"} flex items-center justify-center cursor-pointer border border-gray-300 rounded-md p-1 drop-shadow-sm`}>
        <img src={FILTER_SVG} />
      </div>
      {showOptions &&
        <div
          ref={filterSortRef}
          className="md:min-w-[500px] min-w-full flex flex-col border border-neutralBlue1 absolute top-14 md:right-5 right-0 p-6 bg-white drop-shadow-lg rounded-lg z-40"
        >
          <div className="flex flex-col gap-2 pt-4">
            <div className="flex flex-row gap-3">
              <p className="text-sm font-medium">Filter By Category</p>
              <div
                className="border-2 border-neutralGray3 px-2 text-center text-xs font-normal cursor-pointer rounded-3xl"
                onClick={() => setTemp({ ...temp, subject: "" })}
              >
                Clear
              </div>
            </div>
            <div className="flex sm:flex-row flex-col gap-4 sm:gap-0 justify-between">
              <div
                className={`${temp.subject === 'Account Support' ? 'bg-neutralBlue2' : 'bg-white'
                  } border border-neutralBlue1 basis-[32%] text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, subject: "Account Support" })}
              >
                Account Support
              </div>
              <div
                className={`${temp.subject === 'General Support' ? 'bg-neutralBlue2' : 'bg-white'
                  } border border-neutralBlue1 basis-[32%] text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, subject: "General Support" })}
              >
                General Support
              </div>
              <div
                className={`${temp.subject === 'Payment Support' ? 'bg-neutralBlue2' : 'bg-white'
                  } border border-neutralBlue1 basis-[32%] text-center px-2 py-3 text-sm font-medium cursor-pointer rounded-lg`}
                onClick={() => setTemp({ ...temp, subject: "Payment Support" })}
              >
                Payment Support
              </div>
            </div>
          </div>


          <div className="flex mt-5 gap-7">
            <Button
              text="Clear All"
              block
              type="s-outlined"
              onClick={() => {
                onClear();
              }}
            />
            <Button
              text="Apply"
              block
              type="primary"
              onClick={() => {
                onApply(temp)
                setShowOptions(false);
              }}
            />
          </div>
        </div>
      }
    </div >
  )
}
